import { LegacyCard, Checkbox, FormLayout, Layout, Page, PageActions, Select, TextField, Text, InlineGrid, BlockStack, ChoiceList, Divider, Banner, Card, Box, Button, Listbox, Badge, InlineStack, Icon, SkeletonPage, SkeletonDisplayText, SkeletonBodyText, TextContainer } from '@shopify/polaris';
import { EditIcon, AppExtensionIcon, DiscountIcon, ProductFilledIcon, ProductIcon, CartIcon, DeliveryIcon } from '@shopify/polaris-icons';

import React, { useState, useCallback, useEffect } from 'react';
import { useParams } from "react-router-dom";

import { SkeletonPageCustom } from './SkeletonPageCustom';
import { ColorPickerCustom } from './ColorPickerCustom';
import { QuantityFieldCustom } from './QuantityFieldCustom';
import { FetchCustom } from './FetchCustom';
import { Knob } from './Knob';
import { useHostNavigate } from "../hooks";

export function Widget(props) {
    const params = useParams();
    const navigate = useHostNavigate();

    const [loading, setLoading] = useState(true);
    const [initState, setInitState] = useState({});
    const [model, setModel] = useState({});
    const [processing, setProcessing] = useState(false);
    const [redirectUrl, setRedirectUrl] = useState('');
    const [error, setError] = useState({ autoOpenAfterSeconds: false, animateOncePerSessionAfterSeconds: false, animateOncePerPageLoadAfterSeconds: false, animateEverySeconds: false });

    const fetch = FetchCustom();

    const populateData = async () => {
        props.setIsLoading(true);

        const data = await fetch(`api/home/widget?slug=${params.slug}`);

        if (data.reauthorizeUrl) {
            return;
        }

        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setInitState(data);
            setModel(data);
            setLoading(false);
        }
    };

    const getUser = async () => {
        if (!props.userLoaded) {
            props.setUserLoaded(true);
            const data = await fetch('api/home/userInfo');

            if (data.redirectUrl) {
                setRedirectUrl(data.redirectUrl);
            }
            else if (JSON.stringify(props.user) !== JSON.stringify(data)) {
                props.setUser(data);
                localStorage.setItem('user', JSON.stringify(data));
            }
        }
    };

    const handleChange = useCallback(
        (fieldName, newValue) => {
            if (fieldName === 'launcherAnimationType' || fieldName === 'discountDisplay' || fieldName === 'salePriceHandling' || fieldName === 'discountAllocationPriority') {
                newValue = parseInt(newValue);
            }

            setModel((prevState) => {
                let model = Object.assign({}, prevState);
                model[fieldName] = newValue;
                return model;
            });
        },
        [],
    );

    const handleError = useCallback(
        (fieldName, hasError) => {
            setError((prevState) => {
                let error = Object.assign({}, prevState);
                error[fieldName] = hasError;
                return error;
            });
        },
        [],
    );

    useEffect(() => {
        getUser();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (JSON.stringify(props.user) !== '{}') {
            document.title = "Widget | Stack Discounts";
            populateData();
        }
        // eslint-disable-next-line
    }, [props.user, params.slug]);

    useEffect(() => {
        if (redirectUrl) {
            navigate(redirectUrl);
        }
    }, [redirectUrl, navigate]);

    const renderAnimateOncePerSessionAfterSeconds = useCallback(
        (isSelected) =>
            isSelected && model !== null && model.animateLauncher && (
                <div className="choice-list-input">
                    <QuantityFieldCustom
                        fieldName="animateOncePerSessionAfterSeconds"
                        value={model.animateOncePerSessionAfterSeconds}
                        suffix="seconds"
                        error={error.animateOncePerSessionAfterSeconds ? "This field can't be blank" : ''}
                        handleChange={handleChange}
                        handleError={handleError}
                    />
                </div>
            ),
        [handleChange, handleError, model, error.animateOncePerSessionAfterSeconds],
    );

    const renderAnimateOncePerPageLoadAfterSeconds = useCallback(
        (isSelected) =>
            isSelected && model !== null && model.animateLauncher && (
                <div className="choice-list-input">
                    <QuantityFieldCustom
                        fieldName="animateOncePerPageLoadAfterSeconds"
                        value={model.animateOncePerPageLoadAfterSeconds}
                        suffix="seconds"
                        error={error.animateOncePerPageLoadAfterSeconds ? "This field can't be blank" : ''}
                        handleChange={handleChange}
                        handleError={handleError}
                    />
                </div>
            ),
        [handleChange, handleError, model, error.animateOncePerPageLoadAfterSeconds],
    );

    const renderAnimateEverySeconds = useCallback(
        (isSelected) =>
            isSelected && model !== null && model.animateLauncher && (
                <div className="choice-list-input">
                    <QuantityFieldCustom
                        fieldName="animateEverySeconds"
                        value={model.animateEverySeconds}
                        suffix="seconds"
                        error={error.animateEverySeconds ? "This field can't be blank" : ''}
                        handleChange={handleChange}
                        handleError={handleError}
                    />
                </div>
            ),
        [handleChange, handleError, model, error.animateEverySeconds],
    );

    const navigation = (
        <div className="listbox-navigation">
            <Card padding="100">
                <Box paddingBlock="100">
                    <Listbox onSelect={(value) => { setLoading(true); navigate(value); }}>
                        <Listbox.Action value="/widgets" divider>
                            <InlineStack gap="200">
                                <Icon source={AppExtensionIcon} tone="primary" />
                                <Text fontWeight="medium">General settings</Text>
                            </InlineStack>
                        </Listbox.Action>
                        <Listbox.Action value="/widgets/sticky" selected={params.slug === 'sticky'}>
                            <InlineStack gap="200">
                                <Icon source={DiscountIcon} tone="primary" />
                                <Text fontWeight="medium">Sticky widget</Text>
                            </InlineStack>
                        </Listbox.Action>
                        <Listbox.Action value="/widgets/product-page" selected={params.slug === 'product-page'}>
                            <InlineStack gap="200">
                                <Icon source={ProductIcon} tone="primary" />
                                <Text fontWeight="medium">Product Page widget</Text>
                            </InlineStack>
                        </Listbox.Action>
                        <Listbox.Action value="/widgets/cart-page" selected={params.slug === 'cart-page'}>
                            <InlineStack gap="200">
                                <Icon source={CartIcon} tone="primary" />
                                <Text fontWeight="medium">Cart Page widget</Text>
                            </InlineStack>
                        </Listbox.Action>
                        <Listbox.Action value="/widgets/shipping-bar" selected={params.slug === 'shipping-bar'}>
                            <InlineStack gap="200">
                                <Icon source={DeliveryIcon} tone="primary" />
                                <Text fontWeight="medium">Shipping Bar widget</Text>
                            </InlineStack>
                        </Listbox.Action>
                    </Listbox>
                </Box>
            </Card>
        </div>
    );

    return (
        loading ?
            <SkeletonPage>
                <Layout>
                    <Layout.Section variant="oneThird">
                        {navigation}
                    </Layout.Section>
                    <Layout.Section>
                        <LegacyCard sectioned>
                            <TextContainer>
                                <SkeletonDisplayText size="small" />
                                <SkeletonBodyText lines={9} />
                            </TextContainer>
                        </LegacyCard>
                    </Layout.Section>
                </Layout>
            </SkeletonPage>
            :
            <Page title={model.name}
                backAction={{ content: 'Widgets', onAction: () => navigate('/widgets') }}
                secondaryActions={[
                    { content: 'Revert defaults', loading: processing, onAction: () => { /*revertDefaultSettings()*/ } },
                    { content: 'Preview' },
                    { content: 'Enable' }
                ]}>
                <Layout>
                    <Layout.Section variant="oneThird">
                        {navigation}
                    </Layout.Section>
                    <Layout.Section>
                        <BlockStack gap="400">
                            <Card roundedAbove="sm">
                                <Text as="h2" variant="headingSm">
                                    General
                                </Text>
                                <Box paddingBlockStart="200">
                                    <FormLayout>
                                        <BlockStack gap="100">
                                            <InlineGrid columns="1fr auto">
                                                <InlineStack gap="200">
                                                    <Text as="h3" variant="headingSm" fontWeight="medium">State</Text>
                                                    <Badge tone={model.enabled ? "success" : ""}>{model.enabled ? "Enabled" : "Disabled"}</Badge>
                                                </InlineStack>
                                                <Knob
                                                    selected={model.enabled}
                                                    ariaLabel='Example knob'
                                                    onClick={() => handleChange('enabled', !model.enabled)}
                                                />
                                            </InlineGrid>
                                        </BlockStack>
                                        <Divider />
                                        <Checkbox
                                            label="Hide if no active campaigns"
                                            checked={model.hideIfNoActiveCampaigns}
                                            onChange={(newValue) => handleChange('hideIfNoActiveCampaigns', newValue)}
                                            helpText="We will still show the widget here on the preview."
                                        />
                                        <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                            <Checkbox
                                                label="Auto-open"
                                                checked={model.autoOpen}
                                                onChange={(newValue) => handleChange('autoOpen', newValue)}
                                                helpText="Widget will be automatically opened once per browsing session."
                                            />
                                            <QuantityFieldCustom
                                                id="autoOpenAfterSeconds"
                                                label="Auto-open after"
                                                suffix="seconds"
                                                fieldName="autoOpenAfterSeconds"
                                                value={model.autoOpenAfterSeconds}
                                                disabled={!model.autoOpen}
                                                error={error.autoOpenAfterSeconds ? "This field can't be blank" : ''}
                                                handleChange={handleChange}
                                                handleError={handleError}
                                            />
                                        </InlineGrid>
                                        <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                            <ColorPickerCustom
                                                label="Background color"
                                                fieldName="backgroundColor"
                                                value={model.backgroundColor}
                                                handleChange={handleChange}
                                            />
                                            <ColorPickerCustom
                                                label="Close icon color (for mobile)"
                                                fieldName="closeIconColor"
                                                value={model.closeIconColor}
                                                handleChange={handleChange}
                                            />
                                        </InlineGrid>
                                    </FormLayout>
                                </Box>
                            </Card>
                            <Card roundedAbove="sm">
                                <Text as="h2" variant="headingSm">
                                    Launcher
                                </Text>
                                <Box paddingBlockStart="200">
                                    <FormLayout>
                                        <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                            <ColorPickerCustom
                                                label="Background color"
                                                fieldName="launcherBackgroundColor"
                                                value={model.launcherBackgroundColor}
                                                handleChange={handleChange}
                                            />
                                            <ColorPickerCustom
                                                label="Icon color"
                                                fieldName="launcherIconColor"
                                                value={model.launcherIconColor}
                                                handleChange={handleChange}
                                            />
                                        </InlineGrid>
                                        <Divider />
                                        <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                            <Checkbox
                                                label="Show text on launcher"
                                                checked={model.launcherShowText}
                                                onChange={(newValue) => handleChange('launcherShowText', newValue)}
                                            />
                                            <TextField
                                                value={model.launcherText}
                                                onChange={(newValue) => handleChange('launcherText', newValue)}
                                                label="Launcher text"
                                                disabled={!model.launcherShowText}
                                            />
                                        </InlineGrid>
                                        <Divider />
                                        <BlockStack gap="100">
                                            <InlineGrid columns="1fr auto">
                                                <Text as="h3" variant="headingSm" fontWeight="medium">
                                                    Placement
                                                </Text>
                                                <Button
                                                    icon={EditIcon}
                                                    variant="tertiary"
                                                    onClick={() => { }}
                                                    accessibilityLabel="Edit"
                                                />
                                            </InlineGrid>
                                            <Text as="p" variant="bodyMd">
                                                Bottom right (20px 20px)
                                            </Text>
                                        </BlockStack>
                                        <Divider />
                                        <BlockStack gap="100">
                                            <InlineGrid columns="1fr auto">
                                                <Text as="h3" variant="headingSm" fontWeight="medium">
                                                    Animate launcher
                                                </Text>
                                                <Button
                                                    icon={EditIcon}
                                                    variant="tertiary"
                                                    onClick={() => { }}
                                                    accessibilityLabel="Edit"
                                                />
                                            </InlineGrid>
                                            <Text as="p" variant="bodyMd">
                                                Disabled
                                            </Text>
                                        </BlockStack>
                                    </FormLayout>
                                </Box>
                            </Card>
                            <Card roundedAbove="sm">
                                <Text as="h2" variant="headingSm">
                                    Header
                                </Text>
                                <Box paddingBlockStart="200">
                                    <FormLayout>
                                        <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                            <ColorPickerCustom
                                                label="Background color"
                                                fieldName="headerBackgroundColor"
                                                value={model.headerBackgroundColor}
                                                handleChange={handleChange}
                                            />
                                            <ColorPickerCustom
                                                label="Text color"
                                                fieldName="headerTextColor"
                                                value={model.headerTextColor}
                                                handleChange={handleChange}
                                            />
                                        </InlineGrid>
                                        <BlockStack gap="100">
                                            <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                                <TextField
                                                    value={model.headerText}
                                                    onChange={(newValue) => handleChange('headerText', newValue)}
                                                    label="Header text"
                                                />
                                                <div></div>
                                            </InlineGrid>
                                            <Text as="p" tone="subdued">This text will also be used as a combined title for all active automatic campaigns.</Text>
                                        </BlockStack>
                                    </FormLayout>
                                </Box>
                            </Card>
                        </BlockStack>
                    </Layout.Section>
                    <Layout.AnnotatedSection
                        title="Launcher"
                    >
                        <LegacyCard sectioned>
                            <FormLayout>
                                <Checkbox
                                    label="Hide widget"
                                    checked={model.hideWidget}
                                    onChange={(newValue) => handleChange('hideWidget', newValue)}
                                    helpText="The widget will be entirely hidden and the app's functionality will work in background. We will still show the widget here on the preview."
                                />
                                <Checkbox
                                    label="Hide widget if no active campaigns"
                                    checked={model.hideWidgetIfNoActiveCampaigns}
                                    onChange={(newValue) => handleChange('hideWidgetIfNoActiveCampaigns', newValue)}
                                    helpText="We will still show the widget here on the preview."
                                />
                                <Divider />
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <Select
                                        label="Placement"
                                        options={[{ label: 'Bottom right', value: 'bottomRight' }, { label: 'Bottom left', value: 'bottomLeft' }]}
                                        value={model.launcherPlacement}
                                        onChange={(newValue) => handleChange('launcherPlacement', newValue)}
                                    />
                                    <div></div>
                                </InlineGrid>
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <TextField
                                        label="Bottom Offset"
                                        suffix="px"
                                        type="number"
                                        value={model.launcherBottomOffset}
                                        onChange={(newValue) => handleChange('launcherBottomOffset', newValue)}
                                        helpText="Use this to move launcher up or down."
                                    />
                                    {
                                        model.launcherPlacement === 'bottomRight' ?
                                            <TextField
                                                label="Right Offset"
                                                suffix="px"
                                                type="number"
                                                value={model.launcherRightOffset}
                                                onChange={(newValue) => handleChange('launcherRightOffset', newValue)}
                                                helpText="Use this to move launcher left or right."
                                            />
                                            :
                                            <TextField
                                                label="Left Offset"
                                                suffix="px"
                                                type="number"
                                                value={model.launcherLeftOffset}
                                                onChange={(newValue) => handleChange('launcherLeftOffset', newValue)}
                                                helpText="Use this to move launcher left or right."
                                            />
                                    }
                                </InlineGrid>
                                <Divider />
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <Checkbox
                                        label="Auto-open widget"
                                        checked={model.autoOpenWidget}
                                        onChange={(newValue) => handleChange('autoOpenWidget', newValue)}
                                        helpText="Widget will be automatically opened once per browsing session."
                                    />
                                    <QuantityFieldCustom
                                        id="autoOpenAfterSeconds"
                                        label="Auto-open after"
                                        suffix="seconds"
                                        fieldName="autoOpenAfterSeconds"
                                        value={model.autoOpenAfterSeconds}
                                        disabled={!model.autoOpenWidget}
                                        error={error.autoOpenAfterSeconds ? "This field can't be blank" : ''}
                                        handleChange={handleChange}
                                        handleError={handleError}
                                    />
                                </InlineGrid>
                                <Divider />
                                <Checkbox
                                    label="Animate launcher"
                                    checked={model.animateLauncher}
                                    onChange={(newValue) => handleChange('animateLauncher', newValue)}
                                />
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <Select
                                        disabled={!model.animateLauncher}
                                        label="Animation"
                                        options={[{ label: 'Glow', value: 'glow' }, { label: 'Heartbeat', value: 'heartBeat' }]}
                                        value={model.launcherAnimation}
                                        onChange={(newValue) => handleChange('launcherAnimation', newValue)}
                                    />
                                    <div></div>
                                </InlineGrid>
                                <ChoiceList
                                    disabled={!model.animateLauncher}
                                    choices={[
                                        { label: 'Periodically animate every', value: 3, renderChildren: renderAnimateEverySeconds },
                                        { label: 'Animate once per browsing session after', value: 1, renderChildren: renderAnimateOncePerSessionAfterSeconds },
                                        { label: 'Animate once per each page load after', value: 2, renderChildren: renderAnimateOncePerPageLoadAfterSeconds },
                                    ]}
                                    selected={[model.launcherAnimationType]}
                                    onChange={(newValue) => handleChange('launcherAnimationType', newValue[0])}
                                />
                                <Divider />
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <ColorPickerCustom
                                        label="Background color"
                                        fieldName="launcherBackgroundColor"
                                        value={model.launcherBackgroundColor}
                                        handleChange={handleChange}
                                    />
                                    <ColorPickerCustom
                                        label="Icon color"
                                        fieldName="launcherIconColor"
                                        value={model.launcherIconColor}
                                        handleChange={handleChange}
                                    />
                                </InlineGrid>
                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                    <Checkbox
                                        label="Show text on launcher"
                                        checked={model.launcherShowText}
                                        onChange={(newValue) => handleChange('launcherShowText', newValue)}
                                    />
                                    <TextField
                                        value={model.launcherText}
                                        onChange={(newValue) => handleChange('launcherText', newValue)}
                                        label="Launcher text"
                                        disabled={!model.launcherShowText}
                                    />
                                </InlineGrid>
                            </FormLayout>
                        </LegacyCard>
                    </Layout.AnnotatedSection>
                </Layout>
            </Page >
    );
}